<script setup lang="ts">
import { computed } from 'vue'

import {
  getParticipantTeamSide,
  ParticipantWithLogo,
} from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import { IncidentId, Participant } from '@collector/sportsapi-client-legacy'
import {
  useSwappableIncidents,
  useTeamSides,
} from '@desktop/views/Relation/composables'
import IncidentButton from '@desktop/views/Relation/Sports/components/RelationView/IncidentButton.vue'

interface Props {
  incident: Incident
  participant: Participant
  selectedIncidentId?: IncidentId
  reversed?: boolean
}

const props = defineProps<Props>()

// emits
type Emits = {
  select: [[participantId: Participant['id'], incidentId: IncidentId]]
}

defineEmits<Emits>()

// composables
const { getIncidentTeamSide } = useTeamSides()
const { swappableIncidents } = useSwappableIncidents()

// data
const teamSide = getParticipantTeamSide(props.participant)

// computed
const buttonsClass = computed(() => {
  const ids = getSwappableIncidentsId()
  return ids && ids.length > 3 ? 'grid grid-cols-3 gap-y-2' : 'flex'
})

// methods
function getSwappableIncidentsId(): IncidentId[] | undefined {
  const incidentTeamSide = getIncidentTeamSide(props.incident)

  if (!incidentTeamSide) {
    return
  }

  const config = swappableIncidents.value[props.incident.incident_id]

  return incidentTeamSide === teamSide ? config?.sameTeam : config?.rivalTeam
}
</script>

<template>
  <div class="flex flex-col gap-y-3">
    <ParticipantWithLogo
      class="justify-center"
      :participant
      :reversed
      withTeamColors
    />

    <div
      class="gap-x-2"
      :class="buttonsClass"
    >
      <IncidentButton
        v-for="incidentId in getSwappableIncidentsId()"
        :key="incidentId"
        class="w-full"
        size="lg"
        :incidentId
        :selected="selectedIncidentId === incidentId"
        @click="$emit('select', [participant.id, incidentId])"
      >
        <template #default="{ incidentName }">
          <span class="text-xs leading-3 text-wrap @md:text-nowrap">
            {{ incidentName }}
          </span>
        </template>
      </IncidentButton>
    </div>
  </div>
</template>
